import LazyApexChart from '../../client/components/LazyApexChart';
import { BookOpen, Plus } from "lucide-react";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Button } from "../../client/components/ui/button";
import { useDashboardData } from "./DashboardDataContext";
import { chartColors, pnlColor } from "./chart/chartColors";

export default function EquityChartWidget() {
  const {
    chartMode,
    setChartMode,
    t,
    isDark,
    themeId,
    equityCurveData,
    statsStartDate,
    statsEndDate,
    onAddTrade,
    readOnly,
    previewChartHeight,
  } = useDashboardData();
  const chartH = previewChartHeight ?? 220;
  const c = chartColors();
  const lineColor = pnlColor(equityCurveData.totalPnl);

  return (
    <Card className="h-full overflow-hidden">
      <CardSectionHeader
        label={chartMode === "networth" ? t.dashboard.chartModeNetWorth : t.dashboard.chartModePnl}
        isDark={isDark}
        rightContent={
          <div className="flex items-center rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => { setChartMode("pnl"); localStorage.setItem("dashboard_chart_mode", "pnl"); }}
              className={`px-2.5 py-1 transition-colors ${chartMode === "pnl" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t.dashboard.chartModePnl}
            </button>
            <button
              onClick={() => { setChartMode("networth"); localStorage.setItem("dashboard_chart_mode", "networth"); }}
              className={`px-2.5 py-1 transition-colors ${chartMode === "networth" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t.dashboard.chartModeNetWorth}
            </button>
          </div>
        }
      />
      <CardContent className="px-5 pb-5 pt-1 overflow-hidden">
        {equityCurveData.points.length > 0 ? (
          <LazyApexChart
            key={`equity-dash-${statsStartDate?.getTime() ?? "all"}-${chartMode}-${themeId}`}
            options={{
              chart: {
                type: "area" as const,
                toolbar: { show: false },
                background: "transparent",
                animations: { enabled: false },
                foreColor: c.axis,
              },
              stroke: {
                curve: "smooth" as const,
                width: 2.5,
                colors: [lineColor],
              },
              fill: {
                type: "gradient",
                gradient: {
                  shadeIntensity: 1,
                  opacityFrom: isDark ? 0.30 : 0.20,
                  opacityTo: 0.01,
                  stops: [0, 100],
                  colorStops: [
                    { offset: 0, color: lineColor, opacity: isDark ? 0.30 : 0.20 },
                    { offset: 100, color: lineColor, opacity: 0 },
                  ],
                },
              },
              xaxis: {
                type: "datetime" as const,
                min: statsStartDate ? statsStartDate.getTime() : equityCurveData.xMin,
                max: statsEndDate ? statsEndDate.getTime() : equityCurveData.xMax,
                labels: {
                  style: { colors: c.axis, fontSize: "11px" },
                  datetimeUTC: false,
                  format: "dd MMM",
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
              },
              yaxis: {
                tickAmount: 5,
                labels: {
                  style: { colors: c.axis, fontSize: "11px" },
                  formatter: (val: number) => {
                    if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}k`;
                    return val.toFixed(0);
                  },
                },
                forceNiceScale: true,
              },
              grid: {
                borderColor: c.grid,
                strokeDashArray: 4,
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
              },
              tooltip: {
                theme: isDark ? "dark" : "light",
                x: { format: "dd MMM yyyy" },
                y: {
                  formatter: (val: number) => `${val.toFixed(2)} $`,
                  title: { formatter: () => chartMode === "networth" ? t.dashboard.chartModeNetWorth : t.dashboard.chartModePnl },
                },
              },
              markers: { size: 0 },
              dataLabels: { enabled: false },
              legend: { show: false },
            }}
            series={[{ name: chartMode === "networth" ? t.dashboard.chartModeNetWorth : t.dashboard.chartModePnl, data: equityCurveData.points }]}
            type="area"
            height={chartH}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <BookOpen className="h-8 w-8 text-muted-foreground/40" />
            <div>
              <p className="text-sm font-medium">{t.dashboard.noTradesYet}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.dashboard.noTradesYetDesc}</p>
            </div>
            {!readOnly && (
              <Button size="sm" variant="default" className="text-xs" onClick={() => onAddTrade()}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t.dashboard.addTrade}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
